import { VirtualClinicAnalysisOperatingNumberCalculation } from './calculation';
import type { OverrideProperty } from '@bindoc-projects/util-domain-lib';
import type {
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import {
  BdChildResourceHelper
} from '@bindoc-projects/resource-domain-lib';
import type { VirtualClinicAnalysisOperatingNumberTerm } from '../term/term';
import type { VirtualClinicAnalysisClinicsFilter } from '../filter/clinics-filter';
import type {
  VirtualClinicAnalysisOperatingNumberBenchmarkRequestPart,
  VirtualClinicAnalysisOperatingNumberTermSpec,
} from '../../resolving';

export class VirtualClinicAnalysisOperatingNumberBenchmarkingCalculation extends VirtualClinicAnalysisOperatingNumberCalculation {
  public override readonly type: 'benchmarking' | 'single-virtual-clinic' =
    'benchmarking';

  constructor(
    term: VirtualClinicAnalysisOperatingNumberTerm,
    protected _clinicsFilter: VirtualClinicAnalysisClinicsFilter,
  ) {
    super(term);
  }

  public clone(): VirtualClinicAnalysisOperatingNumberBenchmarkingCalculation {
    return new VirtualClinicAnalysisOperatingNumberBenchmarkingCalculation(
      this._term.clone(),
      this._clinicsFilter.clone(),
    );
  }

  public overrideClone(
    override: OverrideProperty[],
  ): VirtualClinicAnalysisOperatingNumberBenchmarkingCalculation {
    return new VirtualClinicAnalysisOperatingNumberBenchmarkingCalculation(
      this._term.overrideClone(override),
      this._clinicsFilter.overrideClone(override),
    );
  }

  override getChildResources(): {
    [resourceName in ShareableType]?: string[];
  } {
    let childResources = super.getChildResources();

    if (this._clinicsFilter) {
      childResources = BdChildResourceHelper.merge(
        childResources,
        this._clinicsFilter.getChildResources(),
      );
    }

    return childResources;
  }

  override getOverrideProperties(tileId: string): OverrideProperty[] {
    const result = super.getOverrideProperties(tileId, undefined);

    if (this._clinicsFilter) {
      result.push(
        ...this._clinicsFilter.getOverrideProperties(tileId, undefined),
      );
    }

    return result;
  }

  override toJSON(): {
    type: 'benchmarking' | 'single-virtual-clinic';
    term: VirtualClinicAnalysisOperatingNumberTermSpec | null;
    clinicsFilter: { type: string; condition: object } | null;
  } {
    return {
      ...super.toJSON(),
      clinicsFilter: this._clinicsFilter ? this._clinicsFilter.toJSON() : null,
    };
  }

  toRequestPart():
    | Omit<VirtualClinicAnalysisOperatingNumberBenchmarkRequestPart, 'groupBy'>
    | null {
    if (this.isValid()) {
      const term = this._term.toJSON();
      const clinicsCondition = this._clinicsFilter
        ? this._clinicsFilter.toRequestBody({})
        : null;

      const requestBody: Omit<
        VirtualClinicAnalysisOperatingNumberBenchmarkRequestPart,
        'groupBy'
      > = {
        type: 'VirtualClinicOperatingNumberBenchmark',
        term: term,
      };

      if (this._clinicsFilter) {
        requestBody.benchmark = {
          virtualClinicCondition: clinicsCondition,
        };
      }

      return requestBody;
    }

    return null;
  }

  get clinicsFilter(): VirtualClinicAnalysisClinicsFilter {
    return this._clinicsFilter;
  }

  set clinicsFilter(value: VirtualClinicAnalysisClinicsFilter) {
    this._clinicsFilter = value;
  }
}
